import mapboxgl from "mapbox-gl";
import { get, writable } from "svelte/store";
import bbox from "@turf/bbox";

import AppStore from "../AppStore";
import AuthStore from "../Auth/Store";

import MapControlType from "../Map/Enums/MapControlType";
import MapViewType from "../Map/Enums/MapViewType";
import InfoCardStore from "../UI/InfoCard/Store";

import GeoHelper from "../Helpers/GeoHelper";
import StringHelper from "../Helpers/StringHelper";
import LoadState from "../Enums/LoadState";
import LngLatFormat from "../Enums/LngLatFormat";
import BoxSelect from "./BoxSelect";

import roundFloat from "../Helpers/Misc/roundFloat";

const MapStore = {
	meta: {
		initialised: writable(false)
	},
	mapbox: {
		instance: writable(null),
		instanceGeocoder: writable(null)
	},
	rois: {
		loadState: writable(LoadState.INIT),
		// TODO: Refactoring
		// We don't ever want parallel arrays.
		// We want ROI objects containing their geometry and other things e.g. markers/popups as properties.
		infoMarkers: writable([])
	},
	homeLocation: {
		lng: writable(25),
		lat: writable(25),
		zoom: writable(2)
	},
	cursor: writable({
		lng: 0,
		lat: 0
	}),

	control: writable(MapControlType.PAN),
	controlData: {
		lngLatPicker: writable(null),
		lngLatPickerAuto: writable(false),
		boxSelect: new BoxSelect(),
		boxPicker: {
			visible: writable(false),
			obj: new BoxSelect()
		},
		draw: {
			points: writable([]),
			markers: writable([])
		},
		customGeojson: {
			visible: writable(false),
			layers: {
				input: writable([])
			}
		}
	},
	view: writable(MapViewType.ALERTS),
	sanctionsVisible: writable(false),
	timezoneVisible: writable(false),
	hurricanesVisible: writable(false),

	actions: {},
	helpers: {}
};

MapStore.actions.setViewType = (mapViewType) => {
	MapStore.view.set(mapViewType);
};
MapStore.actions.setCursorLngLat = (lng, lat) => {
	lng = (lng > 180) ? lng - 360 : lng;
	lng = (lng < -180) ? lng + 360 : lng;
	lng = roundFloat(lng, 3);
	lat = roundFloat(lat, 3);
	MapStore.cursor.set({
		lng: lng,
		lat: lat
	});
};

MapStore.helpers.getMap = () => {
	return get(MapStore.mapbox.instance);
};
MapStore.helpers.generateRectCoordsFromExtent = (extent) => {
	if (typeof extent === "string") {
		extent = extent.split(",");
		extent[0] = parseFloat(extent[0]);
		extent[1] = parseFloat(extent[1]);
		extent[2] = parseFloat(extent[2]);
		extent[3] = parseFloat(extent[3]);
	}
	return [
		[extent[0], extent[3]], // bl
		[extent[1], extent[3]], // br
		[extent[1], extent[2]], // tr
		[extent[0], extent[2]] 	// tl
	];
};
MapStore.helpers.getCenterLngLat = () => {
	const map = MapStore.helpers.getMap();
	const centerPixels = MapStore.helpers.getCenterPixels();
	const result = map.unproject(centerPixels);
	// console.log("centerPixels", centerPixels, "result", result);
	if (result.lat >= -0.01 && result.lat <= 0.01) {
		result.lat = 0;
	}
	return result;
};
MapStore.helpers.getCenterPixels = () => {
	const map = MapStore.helpers.getMap();
	const center = map.getCenter();
	const centerInPixels = map.project(center);
	centerInPixels.x += AppStore.helpers.getMenuOffset() * 0.5;
	return centerInPixels;
};
MapStore.helpers.zoomToFeature = (feature, padding = 40) => {
	const coords = bbox(feature);
	const bounds = new mapboxgl.LngLatBounds([coords[0], coords[1]], [coords[2], coords[3]]);
	// MapStore.helpers.zoomToCoordinateListBounds(bboxcoords, padding);
	const map = MapStore.helpers.getMap();
	const paddingObj = MapStore.helpers._createPaddingObj(padding);
	map.fitBounds(bounds, paddingObj);
};
MapStore.helpers.createBoundsFromCoordinatesList = (coordinates) => {
	const xCoordinatesList = coordinates[0].map((item) => {
		return item[0];
	});
	const yCoordinatesList = coordinates[0].map((item) => {
		return item[1];
	});

	const xMin = Math.min(...xCoordinatesList);
	const yMin = Math.min(...yCoordinatesList);

	const xMax = Math.max(...xCoordinatesList);
	const yMax = Math.max(...yCoordinatesList);

	const bounds = coordinates.reduce(function (bounds, coord) {
		return bounds.extend(coord);
	}, new mapboxgl.LngLatBounds([xMin, yMin], [xMax, yMax]));
	return bounds;
};
MapStore.helpers.zoomToCoordinateListBounds = (coordinates, padding = 40) => {
	const bounds = MapStore.helpers.createBoundsFromCoordinatesList(coordinates);
	const map = MapStore.helpers.getMap();
	const paddingObj = MapStore.helpers._createPaddingObj(padding);
	map.fitBounds(bounds, paddingObj);
};

MapStore.helpers.calculateFeaturesArrayBounds = (features) => {
	const xCoordinatesList = [];
	const yCoordinatesList = [];

	for (let i = 0; i < features.length; i++) {
		// if (typeof features[i].geometry.coordinates === "object" &&
		// 		features[i].geometry.coordinates.length === 2) {
		// 	xCoordinatesList.push(features[i].geometry.coordinates[0]);
		// 	yCoordinatesList.push(features[i].geometry.coordinates[1]);
		// }
		// else {
		// 	for (let j = 0; j < features[i].geometry.coordinates.length; j++) {
		// 		if (typeof features[i].geometry.coordinates[j] === "object" &&
		// 				features[i].geometry.coordinates[j].length === 2) {
		// 			xCoordinatesList.push(features[i].geometry.coordinates[j][0]);
		// 			yCoordinatesList.push(features[i].geometry.coordinates[j][1]);
		// 		}
		// 		else {
		// 			for (let k = 0; k < features[i].geometry.coordinates[j].length; k++) {
		// 				if (typeof features[i].geometry.coordinates[j][k] === "object" &&
		// 						features[i].geometry.coordinates[j][k].length === 2) {
		// 					xCoordinatesList.push(features[i].geometry.coordinates[j][k][0]);
		// 					yCoordinatesList.push(features[i].geometry.coordinates[j][k][1]);
		// 				}
		// 				else {
		// 					// ...
		// 				}
		// 			}
		// 		}
		// 	}
		// }
		GeoHelper.forEachFeatureCoordinate(features[i], (lng, lat) => {
			xCoordinatesList.push(lng);
			yCoordinatesList.push(lat);
		});
	}
	const finalXCoords = xCoordinatesList;
	const finalYCoords = yCoordinatesList;

	if (finalXCoords.length === 0 || finalYCoords.length === 0) {
		console.warn("calculateFeaturesArrayBounds: no points from line list.");
		return null;
	}

	const xMin = Math.min(...finalXCoords);
	const yMin = Math.min(...finalYCoords);

	const xMax = Math.max(...finalXCoords);
	const yMax = Math.max(...finalYCoords);

	return [[xMin, yMin], [xMax, yMax]];
};
MapStore.helpers.zoomToFeaturesArray = (features, padding = 160) => {
	const bounds = MapStore.helpers.calculateFeaturesArrayBounds(features);
	if (bounds === null) {
		return;
	}
	const xMin = bounds[0][0];
	const yMin = bounds[0][1];

	const xMax = bounds[1][0];
	const yMax = bounds[1][1];

	MapStore.helpers.zoomToLine([xMin, yMin], [xMax, yMax], padding);
};
MapStore.helpers.moveToPoint = (lngLatArray) => {
	const map = MapStore.helpers.getMap();
	map.easeTo({ center: lngLatArray });
};
MapStore.helpers.zoomToPoint = (coords, zoomLevel = 7) => {
	const map = MapStore.helpers.getMap();
	const cardHeight = (get(InfoCardStore.visible))
		? get(InfoCardStore.element).children[0].offsetHeight
		: 0;
	map.flyTo({
		center: coords,
		offset: [AppStore.helpers.getMenuOffset() * 0.5, cardHeight * -0.5],
		speed: 0.5,
		curve: 2,
		zoom: Math.max(map.getZoom(), zoomLevel)
	});
};
// [lng,lat], [lng,lat], num
MapStore.helpers.zoomToLine = (lngLat1, lngLat2, padding = 160) => {
	const map = MapStore.helpers.getMap();
	const bounds = new mapboxgl.LngLatBounds(lngLat1, lngLat2);
	const paddingObj = MapStore.helpers._createPaddingObj(padding);
	map.fitBounds(bounds, paddingObj);
};

MapStore.helpers._createPaddingObj = (padding = 160) => {
	return {
		padding: {
			top: padding,
			left: AppStore.helpers.getMenuOffset() + padding,
			bottom: padding,
			right: padding
		}
	};
};

MapStore.helpers.getDefaultCursor = () => {
	// We will probably want to set the cursor default based on some state, so we do not define it as a constant.
	return "crosshair";
};

// successCallback = (result) => {}
MapStore.helpers.startSingleLngLatPick = (successCallback) => {
	MapStore.controlData.lngLatPickerAuto.set(true);

	MapStore.control.set(MapControlType.LNGLAT_PICKER);
	const unsubscribe = MapStore.controlData.lngLatPicker.subscribe((lngLat) => {
		// console.log(lngLat);
		if (lngLat == null) {
			return;
		}
		const user = get(AuthStore.session.user);
		let lngStr = lngLat.lng;
		let latStr = lngLat.lat;
		if (user.lngLatFormat === LngLatFormat.DMS) {
			lngStr = StringHelper.formatDecimalToDMS(lngLat.lng);
			latStr = StringHelper.formatDecimalToDMS(lngLat.lat, false);
		}

		successCallback(lngStr + ", " + latStr);
		MapStore.controlData.lngLatPickerAuto.set(false);

		setTimeout(() => {
			unsubscribe();
			MapStore.controlData.lngLatPicker.set(null);
		}, 1);
	});
};

// TODO: consider moving to a map helper.
MapStore.helpers.addDataLayer = (name, type, layout, paint, featureCollection, addToArray = null) => {
	if (addToArray == null) {
		addToArray = [];
	}
	const map = MapStore.helpers.getMap();
	map.addSource(name, {
		type: "geojson",
		data: featureCollection
	});
	map.addLayer(
		{
			id: name,
			type: type,
			source: name,
			layout: layout,
			paint: paint
		},
		"waterway-label" // MapLayer.VESSELS_NEARBY_LINES
	);
	addToArray.push(name);
};
MapStore.helpers.printLayers = () => {
	const map = MapStore.helpers.getMap();
	const layers = map.getStyle().layers;
	console.log("layers", layers);
};

// MapStore.helpers.addLayerBefore = (layerId) => {
// 	let layers = map.getStyle().layers;
// 	let firstLayerId;
// 	for (var i = 0; i < layers.length; i++) {
// 		if (layers[i].id === layerId) {
// 			firstLayerId = layers[i].id;
// 			break;
// 		}
// 	}
// };

export default MapStore;
